import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import { withRetry, isRetryableError } from "@/lib/supabase-retry";
import { safeRemoveChannel } from "@/lib/supabase-channel";
import { logDedupError, isOnline } from "@/lib/error-dedup";

interface PartialPayment {
  id: string;
  order_id: string;
  payment_method_id: string;
  amount: number;
  fee_amount: number;
  payer_name: string | null;
  notes: string | null;
  created_at: string;
  created_by: string;
  payment_method?: {
    id: string;
    name: string;
    type: string;
    fee_percentage: number;
  };
}

interface CreatePartialPaymentData {
  orderId: string;
  paymentMethodId: string;
  amount: number;
  feeAmount?: number;
  payerName?: string;
  notes?: string;
}

const FETCH_DEBOUNCE_MS = 1000;

export const usePartialPayments = (orderId: string | null) => {
  const { activeRestaurant } = useRestaurantContext();
  const [partialPayments, setPartialPayments] = useState<PartialPayment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPaid, setTotalPaid] = useState(0);
  const lastFetchTime = useRef<number>(0);
  const isFetching = useRef(false);

  const fetchPartialPayments = useCallback(async () => {
    if (!orderId || !activeRestaurant?.id) return;

    // Debounce: prevent cascading requests
    const now = Date.now();
    if (now - lastFetchTime.current < FETCH_DEBOUNCE_MS) {
      return;
    }

    // Prevent concurrent fetches
    if (isFetching.current) {
      return;
    }

    // Skip if offline
    if (!isOnline()) {
      return;
    }

    lastFetchTime.current = now;
    isFetching.current = true;
    setIsLoading(true);

    try {
      const { data, error } = await supabase
        .from("partial_payments")
        .select(`
          *,
          payment_method:payment_methods (
            id,
            name,
            type,
            fee_percentage
          )
        `)
        .eq("order_id", orderId)
        .eq("restaurant_id", activeRestaurant.id)
        .order("created_at", { ascending: true });

      if (error) throw error;

      setPartialPayments(data || []);
      const total = (data || []).reduce((sum, p) => sum + Number(p.amount), 0);
      setTotalPaid(total);
    } catch (error) {
      logDedupError("PartialPayments.fetch", error);
    } finally {
      setIsLoading(false);
      isFetching.current = false;
    }
  }, [orderId, activeRestaurant?.id]);

  useEffect(() => {
    fetchPartialPayments();
  }, [fetchPartialPayments]);

  // Real-time subscription - stable dependencies to prevent loops
  useEffect(() => {
    if (!orderId || !activeRestaurant?.id) return;

    const channel = supabase
      .channel(`partial-payments-${orderId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "partial_payments",
          filter: `order_id=eq.${orderId}`,
        },
        () => {
          // Only fetch if online to prevent error cascades
          if (isOnline()) {
            fetchPartialPayments();
          }
        }
      )
      .subscribe();

    return () => {
      safeRemoveChannel(channel);
    };
  }, [orderId, activeRestaurant?.id]); // Removed fetchPartialPayments from deps to prevent loops

  const createPartialPayment = async (data: CreatePartialPaymentData) => {
    if (!activeRestaurant?.id) {
      toast.error("Restaurante não selecionado");
      return null;
    }

    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user?.id) {
      toast.error("Usuário não autenticado");
      return null;
    }

    try {
      // Use retry for network resilience
      const newPayment = await withRetry(
        async () => {
          const { data: payment, error } = await supabase
            .from("partial_payments")
            .insert({
              restaurant_id: activeRestaurant.id,
              order_id: data.orderId,
              payment_method_id: data.paymentMethodId,
              amount: data.amount,
              fee_amount: data.feeAmount || 0,
              payer_name: data.payerName || null,
              notes: data.notes || null,
              created_by: userData.user.id,
            })
            .select(`
              *,
              payment_method:payment_methods (
                id,
                name,
                type,
                fee_percentage
              )
            `)
            .single();

          if (error) throw error;
          return payment;
        },
        {
          maxRetries: 3,
          baseDelayMs: 500,
          onRetry: (attempt, error) => {
            console.warn(`[PartialPayment] Tentativa ${attempt}/3:`, error);
          }
        }
      );

      toast.success("Pagamento parcial registrado!");
      return newPayment;
    } catch (error: any) {
      console.error("Error creating partial payment:", error);
      
      // Provide more specific error message based on error type
      if (isRetryableError(error)) {
        toast.error("Falha de conexão ao registrar pagamento. Tente novamente.");
      } else {
        toast.error("Erro ao registrar pagamento parcial");
      }
      return null;
    }
  };

  const deletePartialPayment = async (paymentId: string) => {
    try {
      const { error } = await supabase
        .from("partial_payments")
        .delete()
        .eq("id", paymentId);

      if (error) throw error;

      toast.success("Pagamento removido");
      fetchPartialPayments();
    } catch (error) {
      console.error("Error deleting partial payment:", error);
      toast.error("Erro ao remover pagamento");
    }
  };

  return {
    partialPayments,
    totalPaid,
    isLoading,
    createPartialPayment,
    deletePartialPayment,
    refetch: fetchPartialPayments,
  };
};
